import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, token } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (token) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-stretch">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 bg-slate-900 text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-20"
             style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #475569 0, transparent 40%), radial-gradient(circle at 80% 70%, #1e40af 0, transparent 40%)" }} />
        <div className="relative">
          <div className="flex items-center gap-3 mb-12">
            <div className="h-10 w-10 rounded bg-white grid place-items-center">
              <ShieldCheck className="h-5 w-5 text-slate-900" />
            </div>
            <span className="font-display font-extrabold text-2xl tracking-tight">ClaimWerks</span>
          </div>
          <h1 className="font-display text-5xl font-black tracking-tighter leading-[1.05] mb-6">
            Surplus Funds Recovery,<br/>
            <span className="text-emerald-400">end-to-end.</span>
          </h1>
          <p className="text-slate-300 max-w-md leading-relaxed">
            Lead intake to court filing — with Owner approval gates at every outbound step.
            Compliant, auditable, and built for the way you actually run deals.
          </p>
        </div>
        <div className="relative space-y-2 text-xs text-slate-400 font-mono">
          <div>· Every action logged with timestamp</div>
          <div>· State-specific compliance checker (FL §45.033, TX, generic)</div>
          <div>· AI-assisted drafting; you stay in control</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Owner Sign-In</p>
            <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">
              Welcome back.
            </h2>
            <p className="text-sm text-slate-600 mt-2">Sign in to your ClaimWerks console.</p>
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</Label>
              <Input
                id="email" type="email" required value={email}
                data-testid="login-email-input"
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password</Label>
              <Input
                id="password" type="password" required value={password}
                data-testid="login-password-input"
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 h-11"
              />
            </div>
          </div>

          <Button
            type="submit" disabled={busy}
            data-testid="login-submit-btn"
            className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
